import React, { useState } from "react";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { kapasitetApi } from "@/lib/kapasitet";
import { Card, Help } from "@/components/plan/settings/bits";

const btn = "inline-flex h-9 items-center gap-2 rounded-full border border-black/[0.1] bg-white px-3.5 text-[13px] font-medium hover:border-black/[0.25] disabled:opacity-40";

/** Demo load lives here and nowhere else: what it contains, how to remove it, how to put it back for a demo. */
export function DemoTab({ demoRader, canWrite, onChanged }) {
  const [busy, setBusy] = useState(null);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(msg(r.data)); onChanged?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const remove = () => { if (!window.confirm("Fjerne alt demobelegg fra planen?")) return; run("del", kapasitetApi.delDemo, (d) => `${d.slettet} demorader fjernet`); };
  const seed = () => { if (demoRader && !window.confirm("Legge inn demobelegget på nytt? Eksisterende demorader erstattes.")) return; run("seed", kapasitetApi.seedDemo, (d) => `${d.lagt_inn} demorader lagt inn`); };
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="plan-settings-demo">
      <Card className="p-4">
        <div className="text-[13px] font-semibold">Demobelegg i planen</div>
        <p className="mt-1 text-[12px] text-muted-foreground" data-testid="plan-demo-count">{demoRader ? `${demoRader} demorader ligger i planen, merket «demo» i grunnlaget og i registeret.` : "Ingen demorader i planen."}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={remove} disabled={!canWrite || busy || !demoRader} className={btn} data-testid="plan-demo-remove">{busy === "del" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Fjern demobelegg</button>
          <button type="button" onClick={seed} disabled={!canWrite || busy} className={btn} data-testid="plan-demo-seed">{busy === "seed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Legg inn demobelegg på nytt</button>
        </div>
        <Help>Demobelegget plasseres relativt til denne uken, så demoen ser levende ut uansett når den vises. Demorader beholder «demo»-merket også etter redigering og forsvinner samlet når du fjerner eller legger inn på nytt.</Help>
      </Card>
      <Card className="p-4">
        <div className="text-[13px] font-semibold">Hva demoen inneholder</div>
        <ul className="mt-2 space-y-1.5 text-[12.5px] text-foreground/80">
          <li><span className="font-medium">Seks bekreftede oppdrag</span> – klassing i flytedokk, rørfornying ved kai, thrusteroverhaling på slipp, ventilrevisjon, landstrøm/batteri, løpende serviceavtaler.</li>
          <li><span className="font-medium">Tre reservasjoner</span> med utløpsdato – én utløper om tre dager.</li>
          <li><span className="font-medium">Fire åpne tilbud</span> fra «dagens system» med sannsynlighet – vises bare som prognose.</li>
          <li><span className="font-medium">Én bevisst konflikt</span> på slippen (to oppdrag overlapper) – for å vise hvordan konflikter løses.</li>
        </ul>
        <Help>Ingen demorad er knyttet til en sak i Arbeid. Reelle oppdrag registreres under Plan → Belegg eller importeres fra dagens system.</Help>
      </Card>
    </div>
  );
}
